/**
 * Standalone utility for processing incoming messages for decryption.
 *
 * Handles all wire formats:
 *   - New (ECDH): Base64(IV):Base64(ciphertext):Base64(signature)
 *   - Legacy (RSA): JSON { ciphertext, keyId, algorithm, ... }
 *   - Group: Base64(IV):Base64(ciphertext):Base64(signature)
 *   - Group key distribution: JSON { cls: "encrypted_group_key", group_name, content }
 *     (may also arrive ECDH-encrypted at the outer level — detected post-decryption)
 *   - Group key request: JSON { cls: "group_key_request", group_name }
 *
 * For ECDH messages, `peerUsername` must be provided so the peer's public
 * encryption key can be retrieved from IndexedDB for shared-secret derivation.
 * For group messages, `peerUsername` carries the group's routing address.
 * If it's missing, the message is shown as unreadable rather than silently dropped.
 */

import {
  isKeyExchangeMessage,
  parseKeyExchangeMessage,
  handleReceivedPublicKey,
  refreshPublicKey,
} from "@/services/keyExchangeService";
import {
  getActiveKeySet,
  getPublicKeySetByUsername,
  getSymmetricKey,
  markGroupKeysStale,
  GroupSymmetricKey,
  PeerPublicKeySet,
} from "@/services/keyStorageService";
import {
  decryptMessage,
  isEncryptedMessage,
  isEcdhWireFormat,
  parseEncryptedMessage,
  isGroupMessageWireFormat,
  decryptGroupMessage,
  authenticateGroupMessage,
} from "@/utils/crypto";
import {
  isGroupKeyDistributionMessage,
  parseGroupKeyDistributionMessage,
  receiveGroupKeyDistributionMessage,
  normalizeGroupAddress,
  isGroupKeyRequestMessage,
} from "@/services/groupKeyService";
import { isPhotoMessage } from "@/utils/utils";
import { parseMessageTime } from "@/utils/time";
import { DECRYPT_STATUS_TEXT } from "@/constants/ui";
import type { DecryptStatus } from "@/types/message";

export interface ProcessedMessage {
  originalMessage: string;
  decryptedMessage: string;
  isEncrypted: boolean;
  isKeyExchange: boolean;
  isDecrypting: boolean;
  /** Set for group messages — the sender's username resolved from routing metadata. */
  senderUsername?: string;
  /** Group message decrypted, but its sender's signature couldn't be verified (shown, marked). */
  isUnverified?: boolean;
  /**
   * Set when group message signature verification fails with the cached key.
   * Contains the sender's username so the middleware can fetch a fresh signing
   * key and reload (covers the Android multi-device re-login case).
   */
  needsKeyRefresh?: string;
  /**
   * Set when our group key must be requested from the owner — missing (live only) or proven
   * stale. Contains the group routing address, for requestGroupKey.
   */
  needsGroupKeyRefresh?: string;
  /**
   * The address key material was stored for (peer username for @keys, canonical group
   * address for a distribution) — unset when the event carried nothing usable. Scopes
   * the per-address retry gates via keyExchangeReceived.
   */
  keyArrivedFor?: string;
  /** With keyArrivedFor: the key replaced a different one (peer rotated) — re-decrypt that conversation. */
  keyChanged?: boolean;
  /** Set when the message could not be decrypted (see DecryptStatus). */
  decryptStatus?: DecryptStatus;
}

/** Records why the message isn't readable (logic reads the status, never the text) and its placeholder. */
function markUndecrypted(result: ProcessedMessage, status: DecryptStatus): void {
  result.decryptStatus = status;
  result.decryptedMessage = DECRYPT_STATUS_TEXT[status];
}

/** Web Crypto reports a wrong key (AES-GCM tag mismatch) as OperationError; malformed input fails otherwise. */
const isKeyMismatch = (error: unknown) => (error as { name?: string } | null)?.name === "OperationError";

/**
 * After a group key mismatch: "oldKey" when our key is provably current (the message used a
 * replaced one), "stale" when provably superseded (marked; ask the owner), else "newKey" (unknown).
 * Android owners stamp group messages with their key-pair timestamp, which decides it outright;
 * unstamped, our key is current if the owner's @keys was fetched after the message. Never throws.
 */
async function classifyGroupFailure(
  keyEntry: GroupSymmetricKey,
  keysVersion: number | undefined,
  sentAt: number
): Promise<"oldKey" | "stale" | "newKey" | "error"> {
  try {
    const ownerKeys = await getPublicKeySetByUsername(keyEntry.ownerUsername);
    const stamp = Number(keysVersion);
    const ownerTimestamp = Number(ownerKeys?.timestampGenerated);
    if (stamp > 0 && ownerTimestamp > 0) {
      if (stamp < ownerTimestamp) return "oldKey";
      await markGroupKeysStale((key) => key.groupAddress === keyEntry.groupAddress);
      return "stale";
    }
    // No owner key held means we are the owner (members need it to unwrap) — ours is authoritative.
    if (!ownerKeys || (!keyEntry.stale && ownerKeys.receivedAt > sentAt)) return "oldKey";
    return "newKey";
  } catch (err) {
    console.warn("[GroupKey] Group decrypt-failure check failed:", err);
    return "error";
  }
}

/**
 * Processes a raw message string: handles key exchange, ECDH decryption,
 * legacy RSA decryption, group message decryption, group key distribution,
 * group key requests, and plaintext pass-through.
 *
 * @param message        - Raw message content
 * @param userId         - Current user's ID (for own key lookup)
 * @param peerUsername   - Address/username of the other party (required for ECDH decrypt).
 *                         For group messages this is the group's routing address.
 * @param senderUsername - Sender's username resolved from message metadata (used for
 *                         3-part Android group messages where it isn't embedded in the cipher)
 * @param createdAt      - The message's `created` (router or ISO time); defaults to now (live)
 */
export async function processIncomingMessage(
  message: string,
  userId: number,
  peerUsername?: string,
  senderUsername?: string,
  keysVersion?: number,
  isHistory = false,
  createdAt?: string,
): Promise<ProcessedMessage> {
  const parsedCreated = createdAt ? parseMessageTime(createdAt) : NaN;
  const sentAt = Number.isFinite(parsedCreated) ? parsedCreated : Date.now();
  const result: ProcessedMessage = {
    originalMessage: message,
    decryptedMessage: message,
    isEncrypted: false,
    isKeyExchange: false,
    isDecrypting: false,
  };

  // File/media messages (JSON with cls:"mms") are never encrypted — pass them through
  // before any wire-format sniffing. Without this, every attachment's multi-megabyte
  // base64 JSON gets JSON.parse'd and scanned repeatedly by the detectors below, which
  // dominates the load time of attachment-heavy conversations.
  if (isPhotoMessage(message)) {
    if (senderUsername) result.senderUsername = senderUsername;
    return result;
  }

  // Group key distribution message (arrives as a 1:1 message from the group owner)
  if (isGroupKeyDistributionMessage(message)) {
    result.isKeyExchange = true;
    result.decryptedMessage = "🔑 Group key received";
    // Skip in history — applied when it arrived live, or by the login catch-up (catchUpGroupKeys).
    // Re-attempting the ECDH decrypt on every conversation open is expensive and
    // produces spurious errors if the web's own key has since changed.
    if (!isHistory) {
      const parsed = parseGroupKeyDistributionMessage(message);
      if (parsed && peerUsername) {
        try {
          const storedAt = await receiveGroupKeyDistributionMessage(parsed, userId, peerUsername);
          if (storedAt) {
            result.keyArrivedFor = storedAt;
            console.log(`[E2EE] Stored group key for "${parsed.group_name}" from "${peerUsername}"`);
          } else {
            console.warn(`[E2EE] Group key from "${peerUsername}" for "${parsed.group_name}" NOT stored — see [GroupKey] guard log above`);
          }
        } catch (err) {
          console.error("[E2EE] Failed to store group key:", err);
        }
      }
    }
    return result;
  }

  // Group key request — a non-owner member asking the owner for the symmetric key.
  // The middleware (websocketMiddleware) handles the owner auto-response for live
  // messages. Here we just mark it as a key exchange event so keyExchangeVersion
  // increments and checkEncryption re-runs.
  if (isGroupKeyRequestMessage(message)) {
    result.isKeyExchange = true;
    result.decryptedMessage = "🔑 Group key requested";
    return result;
  }

  // Detect whether peerUsername is a group routing address (JSON with gid field).
  // Used to identify 3-part Android group messages that look like ECDH otherwise.
  const isGroupContext = (() => {
    if (!peerUsername) return false;
    try { const p = JSON.parse(peerUsername); return typeof p?.gid !== "undefined"; }
    catch { return false; }
  })();

  // Group message: 3-part format, or legacy 4-part in a group conversation.
  if (isGroupMessageWireFormat(message, isGroupContext)) {
    result.isEncrypted = true;
    result.isDecrypting = true;
    const groupAddress = peerUsername;
    // Surface the sender up front (from the caller-resolved param) so the username renders
    // on a live failure bubble too — not only after a refresh.
    // The success path below overrides this with the embedded/effective sender.
    if (senderUsername) result.senderUsername = senderUsername;
    let keyEntry: GroupSymmetricKey | null = null;
    try {
      if (!groupAddress) {
        markUndecrypted(result, "error"); // defensive: callers always pass it
        return result;
      }
      keyEntry = await getSymmetricKey(normalizeGroupAddress(groupAddress));
      if (!keyEntry) {
        markUndecrypted(result, "newKey");
        if (!isHistory) result.needsGroupKeyRefresh = groupAddress;
        return result;
      }
      const { plaintext, senderUsername: embeddedSender } = await decryptGroupMessage(message, keyEntry.symmetricKeyB64);
      // Legacy 4-part messages embed the sender in the cipher; current 3-part format
      // relies on the caller passing it via the senderUsername parameter.
      const effectiveSender = embeddedSender ?? senderUsername;

      // Decrypting proves only that SOME member wrote this; the signature proves which. Do NOT
      // gate on keysVersion (for groups it's the symmetric-key version, not the sender's).
      const senderKeys = effectiveSender ? await getPublicKeySetByUsername(effectiveSender) : null;
      const isAuthentic = !!senderKeys && (await authenticateGroupMessage(message, senderKeys.signPublicKeyJwk));
      if (!isAuthentic) {
        result.isUnverified = true;
        // Re-fetch only if our copy of the sender's key predates the message (or is missing) —
        // an older message signed with a replaced key can't be fixed by a fetch.
        if (effectiveSender && !(senderKeys && senderKeys.receivedAt > sentAt)) result.needsKeyRefresh = effectiveSender;
        if (!isHistory) {
          const why = !effectiveSender ? "sender unknown" : senderKeys ? "signature mismatch" : "no signing key yet";
          console.warn(`[E2EE] Group message from ${effectiveSender ?? "?"} shown unverified (${why})`);
        }
      }

      result.decryptedMessage = plaintext;
      result.senderUsername = effectiveSender ?? undefined;
    } catch (error) {
      const verdict = keyEntry && isKeyMismatch(error) ? await classifyGroupFailure(keyEntry, keysVersion, sentAt) : "error";
      markUndecrypted(result, verdict === "stale" ? "newKey" : verdict);
      if (verdict === "stale") {
        result.needsGroupKeyRefresh = groupAddress;
      } else if (verdict === "newKey" && keyEntry && !isHistory) {
        // Evidence: re-check the owner. A rotation marks our key stale and the next open/send
        // asks; otherwise the fresher copy settles this message on "oldKey".
        void refreshPublicKey(keyEntry.ownerUsername, userId);
      }
      if (!isHistory) console.error("[E2EE] Failed to decrypt group message:", error);
    } finally {
      result.isDecrypting = false;
    }
    return result;
  }

  // Key exchange message from @keys relay
  if (isKeyExchangeMessage(message)) {
    result.isKeyExchange = true;
    result.decryptedMessage = "🔑 Public key received";
    const keyMsg = parseKeyExchangeMessage(message);
    if (keyMsg) {
      if (keyMsg.error) {
        console.warn("[E2EE] Key exchange error:", keyMsg.error, "for:", keyMsg.username);
      } else {
        console.log("[E2EE] Received public key for:", keyMsg.username);
        try {
          const stored = await handleReceivedPublicKey(keyMsg);
          // Stored successfully — a DM with this user can now retry its 🔒 rows.
          if (stored && keyMsg.username) {
            result.keyArrivedFor = keyMsg.username;
            result.keyChanged = stored.keyChanged;
          }
        } catch (err) {
          console.error("[E2EE] Failed to store received public key:", err);
        }
      }
    }
    return result;
  }

  if (!isEncryptedMessage(message)) {
    if (senderUsername) result.senderUsername = senderUsername;
    return result;
  }

  result.isEncrypted = true;
  result.isDecrypting = true;

  let peerKeys: PeerPublicKeySet | null = null;
  try {
    if (isEcdhWireFormat(message)) {
      if (!peerUsername) {
        markUndecrypted(result, "error"); // defensive: callers always pass it
        return result;
      }

      const [ownKeySet, peerKeySet] = await Promise.all([
        getActiveKeySet(userId),
        getPublicKeySetByUsername(peerUsername),
      ]);
      peerKeys = peerKeySet;

      if (!ownKeySet || !peerKeys) {
        markUndecrypted(result, "newKey");
        return result;
      }

      const plaintext = await decryptMessage(
        message,
        ownKeySet.encryptPrivateKeyJwk,
        peerKeys.encryptPublicKeyJwk
      );

      // A group key distribution message may arrive ECDH-encrypted at the outer
      // level. If so, handle it now rather than surfacing the raw JSON in the chat.
      if (isGroupKeyDistributionMessage(plaintext)) {
        result.isKeyExchange = true;
        result.decryptedMessage = "🔑 Group key received";
        if (!isHistory) {
          const parsed = parseGroupKeyDistributionMessage(plaintext);
          if (parsed && peerUsername) {
            try {
              const storedAt = await receiveGroupKeyDistributionMessage(parsed, userId, peerUsername);
              if (storedAt) result.keyArrivedFor = storedAt;
              console.log(`[E2EE] Stored group key for "${parsed.group_name}" from "${peerUsername}"`);
            } catch (err) {
              console.error("[E2EE] Failed to store group key (ECDH-wrapped):", err);
            }
          }
        }
        return result;
      }

      result.decryptedMessage = plaintext;
    } else {
      // Legacy RSA path — old messages from before the EC migration
      const encryptedMsg = parseEncryptedMessage(message);
      if (!encryptedMsg) throw new Error("Invalid legacy message format");

      // RSA private keys are cleared in the DB v2 upgrade — these messages
      // will show as unreadable, which is expected after the migration.
      markUndecrypted(result, "oldKey");
    }
  } catch (error) {
    if (!isHistory) console.error("[E2EE] Failed to decrypt message:", error);
    // Key mismatch: old key only if our copy of the peer's key is newer than the message.
    // Otherwise the peer may have rotated since our fetch — refresh; a changed key reloads.
    if (!isKeyMismatch(error) || !peerKeys) {
      markUndecrypted(result, "error");
    } else if (!(peerKeys.receivedAt > sentAt)) {
      markUndecrypted(result, "newKey");
      if (!isHistory && peerUsername) void refreshPublicKey(peerUsername, userId);
    } else {
      markUndecrypted(result, "oldKey");
    }
  } finally {
    result.isDecrypting = false;
  }

  return result;
}
